import { createFileRoute, Link, Navigate, useBlocker, useNavigate } from "@tanstack/react-router";
import { useState, useRef } from "react";
import { useLingui } from "@lingui/react/macro";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useCanEditRecipes } from "@/hooks/useCanEditRecipes";
import { getApiErrorFields, getApiErrorMessage, type ApiFieldErrors } from "@/lib/api/api";
import { missingForPublish, missingOnTab, tabForFields } from "@/lib/recipes/publish-requirements";
import {
  useAllRecipeIngredients,
  useRecipe,
  useRecipeStages,
  useReplaceRecipeStages,
  useUpdateRecipe,
  useRecipeStorageContainers,
  useRecipeShelfLife,
} from "@/hooks/recipes";
import { numberedSteps } from "@/lib/recipes/staged-method";
import { SaveStepsError, saveStepsKeepingStages } from "@/lib/recipes/save-steps";
import type { RecipeStep } from "@/components/recipes/execution-steps";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AvailabilityTab,
  StorageContainersTab,
  ShelfLifeStorageTab,
  LabelingTab,
  CostFinancialsTab,
  RecipeDetailHeader,
} from "@/components/recipes/detail";
import { IngredientsTab } from "@/components/recipes/detail/tabs/IngredientsTab";
import {
  ExecutionStepsForm,
  GeneralDetailsForm,
  type GeneralDetailsFormHandle,
  PreviewModeBanner,
  RecipeDetailView,
  RecipeMissingInfoBanner,
  RecipeReadyToPublishBanner,
  RecipeTabNav,
  type RecipeTab,
  RecipeEditActionBar,
} from "@/components/recipes";
import { CookModeDialog } from "@/components/recipes/dialogs/CookModeDialog";
import { PublishRecipeDialog } from "@/components/recipes/dialogs/PublishRecipeDialog";
import { UnsavedChangesDialog } from "@/components/recipes/dialogs/UnsavedChangesDialog";
import { ChangeToDraftDialog } from "@/components/recipes/dialogs/ChangeToDraftDialog";
import { RecipeStagesEditorDialog } from "@/components/recipes/method/RecipeStagesEditorDialog";

export const Route = createFileRoute(
  "/(protected)/_layout/recipes/$recipeId_/edit",
)({
  component: RecipeEditPage,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (search.tab as RecipeTab) ?? "general",
  }),
});

function RecipeEditPage() {
  const { t } = useLingui();
  const { recipeId } = Route.useParams();
  const { tab: initialTab } = Route.useSearch();
  const { isAuthenticated, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState<RecipeTab>(initialTab);
  const [preview, setPreview] = useState(false);
  const [cookModeOpen, setCookModeOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [changeToDraftDialogOpen, setChangeToDraftDialogOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  // Which boxes the last save was rejected over. Cleared on the next success,
  // so a fixed field stops being marked wrong (T-704).
  const [generalFieldErrors, setGeneralFieldErrors] = useState<ApiFieldErrors>({});

  // Whether the general form is holding edits the recipe does not have yet, and
  // where a cook was trying to go when they still were (T-707).
  const [generalDirty, setGeneralDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState<RecipeTab | null>(null);

  const generalFormRef = useRef<GeneralDetailsFormHandle>(null);

  // Only the general tab holds edits in the browser; every other tab saves as
  // it goes, so there is nothing there to walk away from.
  const dirty = activeTab === "general" && generalDirty;

  const navigate = useNavigate();
  // Cancel is itself the answer to "are you sure?", so leaving through it must
  // not be met with the prompt that guards an accidental exit.
  const cancelling = useRef(false);

  const blocker = useBlocker({
    shouldBlockFn: () => dirty && !cancelling.current,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  /** Drop whatever is in the form and go back to the recipe. */
  const cancelEditing = () => {
    generalFormRef.current?.reset();
    setGeneralDirty(false);
    cancelling.current = true;
    void navigate({ to: "/recipes/$recipeId", params: { recipeId } });
  };

  const requestTabChange = (next: RecipeTab) => {
    // Switching tabs unmounts the form, which is the same lost work as leaving
    // the page — ask first.
    if (dirty) {
      setPendingTab(next);
      return;
    }
    setActiveTab(next);
  };

  const discardChanges = () => {
    if (pendingTab) {
      generalFormRef.current?.reset();
      setGeneralDirty(false);
      setActiveTab(pendingTab);
      setPendingTab(null);
      return;
    }
    blocker.proceed?.();
  };

  const keepEditing = () => {
    setPendingTab(null);
    blocker.reset?.();
  };

  const { data: recipe, isLoading, error } = useRecipe(parseInt(recipeId), isAuthenticated);
  const canEditRecipes = useCanEditRecipes();
  const updateRecipe = useUpdateRecipe();
  const { data: containersData } = useRecipeStorageContainers(parseInt(recipeId));
  const { data: shelfLifeData } = useRecipeShelfLife(recipeId);
  // The method's stages point at steps by number and at ingredient lines, so
  // the steps tab loads both: to edit them, and to keep them on the right
  // steps when deleting one renumbers the rest.
  const onStepsTab = isAuthenticated && activeTab === "execution";
  const stagesQuery = useRecipeStages(parseInt(recipeId), onStepsTab);
  const linesQuery = useAllRecipeIngredients(parseInt(recipeId), onStepsTab);
  const replaceStages = useReplaceRecipeStages(parseInt(recipeId));

  const canManage = hasPermission("recipe:update") || hasPermission("recipe:*");

  // Recipes are edited in Build mode only (YC-33). Anyone who lands here in
  // Working mode — a bookmark, or the mode switched while the editor was open —
  // goes back to the read-only recipe.
  if (!canEditRecipes) {
    return <Navigate to="/recipes/$recipeId" params={{ recipeId }} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-6 p-6">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-50 w-full rounded-xl" />
        <div className="flex items-start justify-between px-8">
          <div className="space-y-3">
            <Skeleton className="h-10 w-72" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-13 w-24" />
            <Skeleton className="h-13 w-28" />
            <Skeleton className="h-13 w-24" />
          </div>
        </div>
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="flex flex-1 flex-col gap-6 p-6">
        <Link
          to="/recipes/$recipeId"
          params={{ recipeId }}
          className="inline-flex items-center gap-1.5 text-sm text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
        >
          <ArrowLeft className="size-4" />
          {t`Back to Recipe`}
        </Link>
        <div className="flex flex-col items-center gap-4 py-16">
          <p className="text-sm text-(--color-text-secondary)">
            {t`Recipe not found or you don't have permission to edit it.`}
          </p>
        </div>
      </div>
    );
  }

  // T-704: what is missing comes from the API's rule module, so the banner, the
  // tab marks and the publish gate cannot disagree about it. The tabs that are
  // not publish requirements still show whether they have anything in them.
  const missing = missingForPublish(recipe);
  const missingInfo = missing.length > 0;
  const statusOf = (tab: string) => (missingOnTab(missing, tab).length > 0 ? "incomplete" : "complete");

  const tabStatuses = {
    general: statusOf("general"),
    availability: "complete",
    ingredients: statusOf("ingredients"),
    execution: statusOf("execution"),
    containers: (containersData?.data?.length ?? 0) > 0 ? "complete" : "incomplete",
    shelfLife: (shelfLifeData?.data?.length ?? 0) > 0 ? "complete" : "incomplete",
    // A recipe is labelable once a stage exists to draw the use-by from; which
    // fields print is the organization's decision, not this recipe's (T-1002).
    labeling: (shelfLifeData?.data?.some((r) => r.labelingRequired) ?? false)
      ? "complete"
      : "incomplete",
    cost: recipe.targetSellingPrice != null && recipe.targetSellingPrice > 0 ? "complete" : "incomplete",
  } as const;

  return (
    <div className={`flex flex-1 flex-col ${preview ? "gap-5" : "gap-8"}`}>
      <RecipeDetailHeader
        recipe={recipe}
        canManage={canManage}
        mode={preview ? "view" : "edit"}
        onSave={activeTab === "general" ? () => generalFormRef.current?.save() : undefined}
        isSaving={updateRecipe.isPending}
        onPublish={() => setPublishDialogOpen(true)}
        onChangeToDraft={() => setChangeToDraftDialogOpen(true)}
        onPreview={() => setPreview(true)}
        onCookMode={() => setCookModeOpen(true)}
        onBannerChange={(imageUrl) =>
          updateRecipe.mutate(
            { id: recipe.id, data: { imageUrl } },
            {
              onSuccess: () => toast.success(t`Cover image updated`),
              onError: (error) =>
                toast.error(getApiErrorMessage(error, t`Failed to update cover image`)),
            },
          )
        }
        onAvatarChange={(avatarUrl) =>
          updateRecipe.mutate(
            { id: recipe.id, data: { avatarUrl } },
            {
              onSuccess: () => toast.success(t`Recipe photo updated`),
              onError: (error) =>
                toast.error(getApiErrorMessage(error, t`Failed to update recipe photo`)),
            },
          )
        }
      />

      <CookModeDialog
        recipe={recipe}
        open={cookModeOpen}
        onOpenChange={setCookModeOpen}
      />

      <PublishRecipeDialog
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        isPending={updateRecipe.isPending}
        missing={missing}
        onFix={(tab) => {
          setPublishDialogOpen(false);
          setActiveTab(tab as RecipeTab);
        }}
        onConfirm={() => {
          updateRecipe.mutate(
            { id: recipe.id, data: { isPublished: true } },
            {
              onSuccess: () => {
                setPublishDialogOpen(false);
                setGeneralFieldErrors({});
                toast.success(t`Recipe published successfully`);
              },
              // The server has the last word on whether a recipe is ready. If
              // it refuses one the browser thought was fine — a stale list, a
              // field cleared in another tab — take its answer, open the tab
              // it names and mark the boxes (T-705).
              onError: (error) => {
                const fields = getApiErrorFields(error);
                setGeneralFieldErrors(fields);
                const tab = tabForFields(fields);
                if (tab) {
                  setPublishDialogOpen(false);
                  setActiveTab(tab as RecipeTab);
                }
                toast.error(getApiErrorMessage(error, t`Failed to publish recipe`));
              },
            },
          );
        }}
      />

      <ChangeToDraftDialog
        open={changeToDraftDialogOpen}
        onOpenChange={setChangeToDraftDialogOpen}
        isPending={updateRecipe.isPending}
        onConfirm={() => {
          updateRecipe.mutate(
            { id: recipe.id, data: { isPublished: false } },
            {
              onSuccess: () => {
                setChangeToDraftDialogOpen(false);
                toast.success(t`Recipe moved to draft`);
              },
              onError: () => toast.error(t`Failed to update recipe`),
            },
          );
        }}
      />

      {preview ? (
        <>
          <RecipeDetailView recipe={recipe} contentPaddingBottom="pb-24" />
          <PreviewModeBanner onExit={() => setPreview(false)} />
        </>
      ) : (
        <>
          <div className="flex flex-col gap-3 px-3">
            {!recipe.isPublished &&
              (missingInfo ? (
                <RecipeMissingInfoBanner missing={missing} />
              ) : (
                <RecipeReadyToPublishBanner />
              ))}
            <RecipeTabNav
              activeTab={activeTab}
              onTabChange={requestTabChange}
              tabStatuses={tabStatuses}
            />
          </div>

          <div className="px-6 pb-10">
            {activeTab === "general" && (
              <GeneralDetailsForm
                ref={generalFormRef}
                recipe={recipe}
                isSaving={updateRecipe.isPending}
                canManage={canManage}
                fieldErrors={generalFieldErrors}
                missingForPublish={missing}
                onDirtyChange={setGeneralDirty}
                onSave={(data) =>
                  updateRecipe.mutate(
                    { id: recipe.id, data },
                    {
                      onSuccess: () => {
                        setGeneralFieldErrors({});
                        toast.success(t`General details saved`);
                      },
                      // A rejected save says which boxes it was rejected over,
                      // and says nothing about having saved (T-704).
                      onError: (error) => {
                        setGeneralFieldErrors(getApiErrorFields(error));
                        toast.error(getApiErrorMessage(error, t`Failed to save general details`));
                      },
                    },
                  )
                }
              />
            )}
            {activeTab === "availability" && <AvailabilityTab recipe={recipe} />}
            {activeTab === "ingredients" && <IngredientsTab recipe={recipe} canAdd />}
            {activeTab === "execution" && (
              <ExecutionStepsForm
                recipe={recipe}
                onSave={async (steps, renumbered) => {
                  try {
                    await saveStepsKeepingStages({
                      steps,
                      renumbered,
                      previousSteps: recipe.instructions,
                      // `refetch` reports a failure in its result unless asked to throw.
                      loadStages: async () =>
                        stagesQuery.data ?? (await stagesQuery.refetch({ throwOnError: true })).data,
                      saveSteps: (instructions) =>
                        updateRecipe.mutateAsync({ id: recipe.id, data: { instructions: instructions as RecipeStep[] } }),
                      replaceStages: (stages) => replaceStages.mutateAsync(stages),
                    });
                  } catch (error) {
                    const { failure, cause } =
                      error instanceof SaveStepsError ? error : { failure: "steps" as const, cause: error };
                    const message = {
                      load: t`Couldn't load the stages, so the steps weren't saved. Try again.`,
                      steps: t`Failed to save steps`,
                      stages: t`The stages couldn't be moved to the new step numbers, so the steps weren't saved. Try again.`,
                      "stages-unreverted": t`Steps saved, but the stages still point at the old step numbers. Save again before leaving this page.`,
                    }[failure];
                    toast.error(getApiErrorMessage(cause, message));
                    // Rejecting keeps the form's edits and its old numbers, so
                    // the next save from this page can still move the stages.
                    throw error;
                  }
                  toast.success(t`Execution steps saved`);
                }}
                onCancel={cancelEditing}
                isSaving={updateRecipe.isPending || replaceStages.isPending}
                onEditStages={canManage && stagesQuery.data && linesQuery.data ? () => setStagesOpen(true) : undefined}
              />
            )}

            {activeTab === "execution" && canManage && stagesQuery.data && linesQuery.data && (
              <RecipeStagesEditorDialog
                open={stagesOpen}
                onOpenChange={setStagesOpen}
                recipeId={recipe.id}
                stages={stagesQuery.data}
                lines={linesQuery.data.data}
                steps={numberedSteps(Array.isArray(recipe.instructions) ? (recipe.instructions as RecipeStep[]) : [])}
              />
            )}

            {activeTab === "containers" && <StorageContainersTab recipe={recipe} canManage={canManage} />}
            {activeTab === "shelfLife" && <ShelfLifeStorageTab recipe={recipe} canManage={canManage} />}
            {activeTab === "labeling" && <LabelingTab recipe={recipe} canManage={canManage} />}
            {activeTab === "cost" && <CostFinancialsTab recipe={recipe} />}
          </div>

          {/* Save has to stay reachable from the bottom of a long recipe (T-707). */}
          {activeTab === "general" && canManage && (
            <RecipeEditActionBar
              dirty={generalDirty}
              isSaving={updateRecipe.isPending}
              onSave={() => generalFormRef.current?.save()}
              onCancel={cancelEditing}
            />
          )}
        </>
      )}

      <UnsavedChangesDialog
        open={blocker.status === "blocked" || pendingTab !== null}
        onOpenChange={(open) => {
          if (!open) keepEditing();
        }}
        onDiscard={discardChanges}
        onStay={keepEditing}
      />
    </div>
  );
}
